import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import { useAppBar } from '../../providers/AppBarProvider';
import { fetchCashMovement, formatCurrency, handleError } from '../../utils/lib';
import { useLocation, useNavigate } from 'react-router-dom';
import ViewPage from './ViewPage';
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import BottomSheet from '../../layouts/BottomSheet';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { useTranslation } from 'react-i18next';

function ViewCashMovement() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setNavigation } = useNavigationHelperStore();
  const snackbar = useSnackbar();
  const [id, setId] = useState(location.state.id);
  const [cashMovement, setCashMovement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const {updateTitle} = useAppBar();
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const movement = await fetchCashMovement(id);
        setCashMovement(movement);
        updateTitle('', t('view.manualCashMovementTitle'));
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [t]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false
  });

  const handleDeleteOpen = () => {
    setDeleteDialog({
      open: true
    });
  };

  const handleDeleteClose = () => {
    setDeleteDialog({
      open: false
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}cash_movements?id=${id}`,
        { withCredentials: true }
      );
      
      handleDeleteClose();
      navigate(-1);
    } catch (error) {
      handleError(error, snackbar);
    }
  };

  const getMovementTypeInfo = (type) => {
    if (type === 'MANUAL_CASH_IN') {
      return {
        label: t('view.cashMovementIn'),
        color: 'success',
        icon: <ArrowDownwardIcon />
      };
    }
    return {
      label: t('view.cashMovementOut'),
      color: 'error',
      icon: <ArrowUpwardIcon />
    };
  };

  if (isLoading || isLoadingConfig || !cashMovement) {
    return <ViewPage isLoading={true} />;
  }

  const typeInfo = getMovementTypeInfo(cashMovement.type);

  return (
    <ViewPage 
      isLoading={isLoading || isLoadingConfig}
    >
      <Box sx={{ px: 1 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip 
              label={typeInfo.label}
              color={typeInfo.color}
              icon={typeInfo.icon}
              sx={{ mb: 1 }}
            />
            {cashMovement.deleted === 1 && (
              <Chip 
                label={t('view.deleted')}
                color="default"
                sx={{ mb: 1 }}
              />
            )}
          </Box>
          <Typography>{t('view.sellFieldDate')} {new Date(cashMovement.date).toLocaleString()}</Typography>
          <Typography>{t('view.sellFieldUser')} {cashMovement.user.name}</Typography>
          <Typography>{t('common.amount')}: {formatCurrency(cashMovement.amount)}</Typography>
          <Typography>{t('view.fieldPaymentMethod')} {cashMovement.payment_type}</Typography>
          {cashMovement.description && (
            <Typography>{t('view.fieldDescription')} {cashMovement.description}</Typography>
          )}
        </Box>

        <Divider sx={{ marginY: 2 }} />
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('view.deleteCashMovementTitle')}</DialogTitle>
        <DialogContent>
          {t('view.deleteCashMovementMessage', {
            type: cashMovement.type === 'MANUAL_CASH_IN'
              ? t('view.cashMovementTypeInLower')
              : t('view.cashMovementTypeOutLower'),
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <BottomSheet 
        zIndex={1001}
        buttonGroups={[
          {
            label: t('common.actions'), 
            buttons: [
              ...(config.permissions?.actions_permissions?.cash_movements?.delete && cashMovement.deleted !== 1 ? [{
                label: t('common.delete'),
                action: handleDeleteOpen,
                icon: <DeleteIcon />
              }] : []),
            ].filter(Boolean)
          }
        ]}
      />
    </ViewPage>
  );
}

export default ViewCashMovement;